// /frontend/src/components/UI/RecordNav/RecordNav.jsx

import React, { useState, useEffect, useMemo, useRef } from "react";

import {
	Button,
	createListCollection,
	IconButton,
	Input,
	MetreaSelectContent,
	MetreaSelectItem,
	MetreaSelectLabel,
	MetreaSelectRoot,
	MetreaSelectTrigger,
	MetreaSelectValueText,
	Portal,
	Switch,
	MetreaTooltip,
} from "@metrea-ui/v2";
import styles from "./RecordNav.module.css";

import {
	ArrowLeftFilled,
	DismissRegular,
	DeleteLinesFilled,
	DeleteRegular,
	DocumentArrowDownRegular,
	DatabaseArrowRightRegular,
	TabDesktopCopyRegular,
	ArchiveRegular,
	ArchiveArrowBackRegular,
	ArrowClockwiseRegular,
	SaveRegular,
	EraserRegular,
} from "@fluentui/react-icons";

import { mColor } from "src/utils/themeUtils";
import { debugLog } from "src/utils/debugUtils";

const sizeToSelectSize = (size) => {
	if (size === "large") return "md";
	if (size === "small") return "sm";
	if (size === "xs" || size === "2xs") return "xs";
	return size || "sm";
};

const getSelectLabel = (option) => option?.label ?? option?.name ?? String(option?.id ?? "");

const getSelectValue = (option) => String(option?.id ?? option?.value ?? "");

const buildSelectCollectionItems = (options) =>
	(options ?? []).map((option) => ({
		label: getSelectLabel(option),
		value: getSelectValue(option),
		disabled: option?.disabled,
	}));

const defaultSortByOptions = [
	{ disabled: false, id: 11, orderBy: "updatedAt", order: "asc", label: "Update Date (asc)" },
	{ disabled: false, id: 12, orderBy: "updatedAt", order: "desc", label: "Update Date (desc)" },
	{ disabled: false, id: 21, orderBy: "createdAt", order: "asc", label: "Create Date (asc)" },
	{ disabled: false, id: 22, orderBy: "createdAt", order: "desc", label: "Create Date (desc)" },
	{ disabled: false, id: 31, orderBy: "name", order: "asc", label: "Name (asc)" },
	{ disabled: false, id: 32, orderBy: "name", order: "desc", label: "Name (desc)" },
	{ disabled: false, id: 41, orderBy: "status_id", order: "asc", label: "Status (asc)" },
	{ disabled: false, id: 42, orderBy: "status_id", order: "desc", label: "Status (desc)" },
];

const rowsPerPageOptions = [
	{ disabled: false, id: 1, label: "1" },
	{ disabled: false, id: 3, label: "3" },
	{ disabled: false, id: 5, label: "5" },
	{ disabled: false, id: 10, label: "10" },
	{ disabled: false, id: 25, label: "25" },
	{ disabled: false, id: 50, label: "50" },
	{ disabled: false, id: 100, label: "100" },
];

const getSwitchValue = (next) =>
	typeof next === "boolean"
		? next
		: (next?.target?.checked ?? next?.checked ?? next?.detail?.checked ?? next?.value ?? false);

const renderInlineLabel = (text, className = "", extraStyle = undefined) => (
	<span className={className} style={extraStyle}>
		{text}
	</span>
);

export const RecordNav = (props) => {
	const renderCount = useRef(0);
	renderCount.current++;

	const [rowsPerPage, setRowsPerPage] = useState(props.rowsPerPage);
	const [searchKeyword, setSearchKeyword] = useState("");
	const controlledShowInactive = typeof props.showInactive === "boolean" ? props.showInactive : undefined;

	const strEnableAction = props.callFrom && props.callFrom === "Listing" ? "Activate" : "Restore";
	const strDisableAction = props.callFrom && props.callFrom === "Listing" ? "De-activate" : "Archive";

	useEffect(() => {
		setRowsPerPage(props.rowsPerPage);
	}, [props.rowsPerPage]);

	const handleSearchKeywordChange = (e, clear = false) => {
		const keyword = clear ? "" : e.target.value.trim();
		setSearchKeyword(keyword);
		debugLog(7, "[RecordNav][ACTION] handleSearchKeywordChange", { keyword, module: "RecordNav" });
		if (props.onSearchChange) props.onSearchChange(keyword);
	};

	const toggleInactiveHandler = (option) => {
		debugLog(3, "[RecordNav][ACTION] toggleInactiveHandler", { option: option });
		props.onToggleInactive(option.value);
	};

	const handleToggleInactiveChange = (next) => {
		const checked = getSwitchValue(next);
		toggleInactiveHandler({
			value: checked,
			id: checked ? 1 : 0,
			label: checked ? "On" : "Off",
		});
	};

	const clickAddItemHandler = (option) => {
		debugLog(3, "[RecordNav][ACTION] clickAddItemHandler", { option: option });
		props.onAddItem(option);
	};

	const sortByHandler = (option) => {
		debugLog(3, "[RecordNav][ACTION] sortByHandler", { option: option });
		props.onSortBy(option);
	};

	// Allow screens to narrow or relabel the shared sort contract while still
	// keeping the selected option in sync with the parent list state.
	const sortByOptions = useMemo(() => props.sortByOptions ?? defaultSortByOptions, [props.sortByOptions]);

	const surfOrderBy =
		props.orderBy === "updatedAtTS"
			? "updatedAt"
			: props.orderBy === "createdAtTS"
				? "createdAt"
				: props.orderBy;
	const selectedSortId =
		sortByOptions.find((option) => option.orderBy === surfOrderBy && option.order === props.order)?.id ??
		sortByOptions.find(
			(option) =>
				option.orderBy === surfOrderBy &&
				(option.order === undefined || option.order === null || option.order === ""),
		)?.id ??
		sortByOptions.find((option) => option.id === 12)?.id ??
		sortByOptions[0]?.id ??
		"";

	const changeRowsPerPageHandler = (option) => {
		debugLog(3, "[RecordNav][ACTION] changeRowsPerPageHandler", { option: option });
		setRowsPerPage(option?.id ?? rowsPerPage);
		props.onChangeRowsPerPage(option);
	};

	const handleSelectValueChange = (rawValue, options, onSelect) => {
		const selectedOption = options.find((option) => String(option.id) === String(rawValue));
		if (!selectedOption) return;
		onSelect(selectedOption);
	};

	const sortByCollection = useMemo(
		() => createListCollection({ items: buildSelectCollectionItems(sortByOptions) }),
		[sortByOptions],
	);

	const rowsPerPageCollection = useMemo(
		() => createListCollection({ items: buildSelectCollectionItems(rowsPerPageOptions) }),
		[],
	);

	const clickBackToListHandler = (option) => {
		debugLog(3, "[RecordNav][ACTION] clickBackToListHandler", { option: option });
		props.onClickBackToList(option);
	};

	const clickPurgeHandler = (option) => {
		debugLog(3, "[RecordNav][ACTION] clickPurgeHandler", { option: option });
		props.onClickPurge(option);
	};

	const clickDeleteHandler = (option) => {
		debugLog(3, "[RecordNav][ACTION] clickDeleteHandler", { option: option });
		props.onClickDelete(option);
	};

	const clickDataDumpHandler = (option) => {
		debugLog(3, "[RecordNav][ACTION] clickDataDumpHandler", { option: option });
		props.onClickDataDump(option);
	};

	const clickExportHandler = (option) => {
		debugLog(3, "[RecordNav][ACTION] clickExportHandler", { option: option });
		props.onClickExport(option);
	};

	const clickCopyHandler = (option) => {
		debugLog(3, "[RecordNav][ACTION] clickCopyHandler", { option: option });
		props.onClickCopy(option);
	};

	const clickArchiveHandler = (option) => {
		debugLog(3, "[RecordNav][ACTION] clickArchiveHandler", { option: option });
		props.onClickArchive(option, 0);
	};

	const clickUnArchiveHandler = (option) => {
		debugLog(3, "[RecordNav][ACTION] clickUnArchiveHandler", { option: option });
		props.onClickArchive(option, 1);
	};

	const clickRevertHandler = (option) => {
		debugLog(3, "[RecordNav][ACTION] clickRevertHandler", { option: option });
		props.onClickRevert(option);
	};

	const clickSaveHandler = (option) => {
		debugLog(3, "[RecordNav][ACTION] clickSaveHandler", { option: option });
		props.onClickSave(option);
	};

	const tooltipText = (() => {
		const { searchFields = [] } = props;

		if (Array.isArray(props.columns) && props.columns.length > 0) {
			const titles = props.columns
				.filter(
					(col) =>
						searchFields.includes(col.key) &&
						typeof col.title === "string" &&
						col.title.trim() !== "",
				)
				.map((col) => col.title);
			if (titles.length > 0) return `Search by ${titles.join(", ")}`;
		}
		if (Array.isArray(searchFields) && searchFields.length > 0) {
			return `Search by ${searchFields.join(", ")}`;
		}
		return "Search by keyword";
	})();

	const renderActionButton = ({ tooltip, onClick, disabled, testId, children, color, colorPalette }) => {
		const button = (
			<IconButton
				variant="ghost"
				onClick={onClick}
				disabled={disabled}
				aria-label={tooltip || testId || "Record action"}
				className={styles.actionIconButton}
				data-testid={testId}
				color={color}
				colorPalette={colorPalette}
			>
				{children}
			</IconButton>
		);

		if (!tooltip) return button;

		return (
			<MetreaTooltip content={tooltip}>
				{/* Direct v2 buttons follow the Chakra contract (`disabled`), not the old wrapper-era `isDisabled` prop. */}
				{button}
			</MetreaTooltip>
		);
	};

	return (
		<>
			<div className={`py-[12px] m-0 border-0 ${styles.toolbar}`}>
				<div className="m-0 basis-auto w-auto flex flex-column">
					<div className="w-100">
						{props.showRowsPerPage &&
							renderInlineLabel(
								`Showing ${props.count} of ${props.data.length} results`,
								"text-sm leading-none",
							)}
						{!props.showRowsPerPage && (
							<>
								{props.showBack &&
									renderActionButton({
										tooltip:
											props.backToolTip ||
											(props.callFrom !== ""
												? `Back To ${props.callFrom} List`
												: "Back To List"),
										onClick: clickBackToListHandler,
										disabled: props.disabled,
										testId: "item-back-btn",
										children: <ArrowLeftFilled />,
									})}
								{props.showClose &&
									renderActionButton({
										tooltip: "Close",
										onClick: clickBackToListHandler,
										disabled: props.disabled,
										testId: "item-close-btn",
										children: <DismissRegular />,
									})}
							</>
						)}
					</div>
				</div>

				<div className={`p-0 m-0 basis-auto w-auto ${styles.toolbarActions}`}>
					{props.showSearch && (
						<div className={`w-[180px] ${styles.searchControl}`}>
							<div className={styles.searchInputWrap}>
								<MetreaTooltip content={tooltipText}>
									<Input
										size="xs"
										variant="subtle"
										placeholder="Search..."
										value={searchKeyword}
										onChange={(e) => handleSearchKeywordChange(e, false)}
										aria-label="Search by keyword"
										className={styles.searchInput}
									/>
								</MetreaTooltip>
								<MetreaTooltip content="Clear Search">
									<IconButton
										variant="ghost"
										size="xs"
										className={styles.clearButton}
										aria-label="Clear Search"
										onClick={(e) => handleSearchKeywordChange(e, true)}
									>
										<EraserRegular />
									</IconButton>
								</MetreaTooltip>
							</div>
						</div>
					)}

					{props.showToggleInactive && (
						<MetreaTooltip
							content="Show/hide inactive records"
						>
							<div className={styles.toggleControl}>
								<div className={styles.toggleControlRow}>
									{/* Stay backward-compatible with older callers that never passed `showInactive`,
									    but honor a controlled value when a parent does provide one. */}
									<Switch.Root
										size="sm"
										checked={controlledShowInactive}
										onCheckedChange={handleToggleInactiveChange}
									>
										<Switch.HiddenInput />
										<Switch.Control>
											<Switch.Thumb />
										</Switch.Control>
									</Switch.Root>
									{renderInlineLabel("Show inactive", styles.toggleLabel)}
								</div>
							</div>
						</MetreaTooltip>
					)}

					{props.showSortBy && (
						<div className={`${styles.selectControl} ${styles.sortSelect}`}>
							<MetreaSelectRoot
								data-raven-compact-select="true"
								variant="outline"
								name="SortBy"
								collection={sortByCollection}
								size={sizeToSelectSize("xs")}
								width="100%"
								className={styles.compactSelectRoot}
								value={[String(selectedSortId)]}
								onValueChange={(details) => {
									const nextValue = Array.isArray(details?.value)
										? String(details.value[0] ?? "")
										: "";
									// Preserve the existing RecordNav callback contract: parents
									// still receive the matched option object, not a raw string.
									handleSelectValueChange(nextValue, sortByOptions, sortByHandler);
								}}
							>
								<MetreaSelectLabel className={styles.selectLabel}>Sort By</MetreaSelectLabel>
								<MetreaSelectTrigger id="SortBy" className={styles.compactSelectTrigger}>
									<MetreaSelectValueText>
										{(selectedItems) => getSelectLabel(selectedItems[0])}
									</MetreaSelectValueText>
								</MetreaSelectTrigger>
								<Portal>
									<MetreaSelectContent>
										{sortByCollection.items.map((item) => (
											<MetreaSelectItem
												key={item.value}
												item={item}
												disabled={item.disabled}
											>
												{item.label}
											</MetreaSelectItem>
										))}
									</MetreaSelectContent>
								</Portal>
							</MetreaSelectRoot>
						</div>
					)}

					{props.showRowsPerPage && (
						<div className={`${styles.selectControl} ${styles.resultsSelect}`}>
							<MetreaSelectRoot
								data-raven-compact-select="true"
								variant="outline"
								name="NumPerPage"
								collection={rowsPerPageCollection}
								size={sizeToSelectSize("xs")}
								width="100%"
								className={styles.compactSelectRoot}
								value={[String(rowsPerPage)]}
								onValueChange={(details) => {
									const nextValue = Array.isArray(details?.value)
										? String(details.value[0] ?? "")
										: "";
									// Keep rows-per-page on the shared matched-option contract
									// instead of leaking the Select value array upstream.
									handleSelectValueChange(
										nextValue,
										rowsPerPageOptions,
										changeRowsPerPageHandler,
									);
								}}
							>
								<MetreaSelectLabel className={styles.selectLabel}>Results</MetreaSelectLabel>
								<MetreaSelectTrigger id="NumPerPage" className={styles.compactSelectTrigger}>
									<MetreaSelectValueText>
										{(selectedItems) => getSelectLabel(selectedItems[0])}
									</MetreaSelectValueText>
								</MetreaSelectTrigger>
								<Portal>
									<MetreaSelectContent>
										{rowsPerPageCollection.items.map((item) => (
											<MetreaSelectItem
												key={item.value}
												item={item}
												disabled={item.disabled}
											>
												{item.label}
											</MetreaSelectItem>
										))}
									</MetreaSelectContent>
								</Portal>
							</MetreaSelectRoot>
						</div>
					)}

					{props.showAddItem && (
						<div className={`${styles.buttonControl} max-w-[148px]`}>
							<Button
								size="2xs"
								variant="subtle"
								className={styles.addButton}
								data-testid="item-add-btn"
								onClick={clickAddItemHandler}
								disabled={props.disabled}
							>
								Add Item
							</Button>
						</div>
					)}

					{props.addItemAdjacentSlot && (
						<div className={styles.buttonControl}>{props.addItemAdjacentSlot}</div>
					)}

					{props.showPurge &&
						renderActionButton({
							tooltip:
								props.purgeToolTip || (props.callFrom ? `Purge ${props.callFrom}` : "Purge"),
							onClick: clickPurgeHandler,
							disabled: props.disabled,
							colorPalette: "red",
							children: <DeleteLinesFilled color={mColor.Danger} />,
						})}

					{props.showDelete &&
						renderActionButton({
							tooltip:
								props.deleteToolTip ||
								(props.callFrom ? `Delete ${props.callFrom}` : "Delete"),
							onClick: clickDeleteHandler,
							disabled: props.disabled,
							children: <DeleteRegular />,
						})}

					{props.showDataDump &&
						renderActionButton({
							tooltip: props.dataDumpToolTip || "Data Dump (.xlsx)",
							onClick: clickDataDumpHandler,
							disabled: props.disabled,
							children: <DocumentArrowDownRegular />,
						})}

					{props.showExport &&
						renderActionButton({
							tooltip:
								props.exportToolTip ||
								(props.callFrom ? `Export ${props.callFrom} (.json)` : "Export (.json)"),
							onClick: clickExportHandler,
							disabled: props.disabled,
							children: <DatabaseArrowRightRegular />,
						})}

					{props.showCopy &&
						renderActionButton({
							tooltip:
								props.copyToolTip ||
								(props.callFrom ? `Duplicate ${props.callFrom}` : "Duplicate"),
							onClick: clickCopyHandler,
							disabled: props.disabled,
							children: <TabDesktopCopyRegular />,
						})}

					{props.showArchive &&
						renderActionButton({
							tooltip:
								props.archiveToolTip ||
								(props.callFrom ? `${strDisableAction} ${props.callFrom}` : strDisableAction),
							onClick: clickArchiveHandler,
							disabled: props.disabled,
							children: <ArchiveRegular />,
						})}

					{props.showRestore &&
						renderActionButton({
							tooltip:
								props.restoreToolTip ||
								(props.callFrom ? `${strEnableAction} ${props.callFrom}` : strEnableAction),
							onClick: clickUnArchiveHandler,
							disabled: props.disabled,
							children: <ArchiveArrowBackRegular />,
						})}

					{props.showRevert &&
						renderActionButton({
							tooltip:
								props.revertToolTip ||
								(props.callFrom ? `Revert ${props.callFrom}` : "Revert data"),
							onClick: clickRevertHandler,
							disabled: props.disabledRevert ?? props.disabled,
							testId: "item-revert-btn",
							children: <ArrowClockwiseRegular />,
						})}

					{props.showSave &&
						renderActionButton({
							tooltip:
								props.saveToolTip || (props.callFrom ? `Save ${props.callFrom}` : "Save"),
							onClick: clickSaveHandler,
							disabled: (props.disabledSave ?? props.disabled) || props.hasError,
							testId: props.showClose ? "item-details-save-btn" : "item-save-btn",
							children: <SaveRegular />,
						})}
				</div>
			</div>
			{props.showBack && <hr />}
		</>
	);
};

export default RecordNav;
